import { Link } from 'react-router-dom';
import { useState, useCallback, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, sites, articles, faqs } from '@/mocks/siteData';

function HeroSection() {
  return (
    <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20network%20connections%20with%20warm%20orange%20and%20teal%20gradient%20nodes%2C%20modern%20minimalist%20digital%20technology%20background%20with%20connected%20dots%20and%20lines%20forming%20a%20global%20network%20pattern%2C%20clean%20light%20background%20with%20soft%20geometric%20accents%2C%20subtle%20depth%2C%20no%20text&width=1600&height=900&seq=hero-bg-2025&orientation=landscape"
        alt="最新网址导航背景"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
          <i className="ri-sparkling-line text-primary-300 text-sm"></i>
          <span className="text-xs md:text-sm text-background-50 font-medium">实时更新 · 安全可靠 · 每日维护</span>
        </div>
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
          最新网址导航
        </h1>
        <p className="text-sm md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
          汇聚各大平台官方最新网址入口，实时更新、安全可靠。告别失效链接困扰，一键直达目标网站。覆盖搜索引擎、社交媒体、视频平台、购物网站等八大分类。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/search-engine"
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-compass-3-line mr-2"></i>浏览网址大全
          </Link>
          <a
            href="#newsletter"
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 font-medium text-sm hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line mr-2"></i>订阅网址更新
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
          {[
            { value: '100+', label: '收录平台' },
            { value: '8', label: '详细分类' },
            { value: '每日', label: '实时更新' },
            { value: '全免费', label: '永久免费' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl md:text-3xl font-heading font-bold text-background-50">{stat.value}</p>
              <p className="text-xs md:text-sm text-background-50/70 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotSitesSection() {
  const hotSites = sites.filter((s) => s.isHot).slice(0, 8);

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs md:text-sm font-medium text-primary-600 mb-2 uppercase tracking-wide">热门推荐</p>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            热门网址推荐
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            精选用户最常访问的平台最新网址，每日更新维护，确保链接安全有效
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {hotSites.map((site) => (
            <a
              key={site.id}
              href={site.url}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="group p-4 md:p-5 rounded-xl bg-background-100 hover:bg-background-200/70 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <div className={`w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl ${site.iconBg} mb-3 group-hover:scale-110 transition-transform duration-200`}>
                <i className="ri-global-line text-lg text-background-50"></i>
              </div>
              <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-1 whitespace-nowrap">
                {site.name}
              </h3>
              <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                {site.description}
              </p>
              <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                {site.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </a>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/category/search-engine"
            className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-lg border border-background-200/70 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看全部网址 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoryGridSection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs md:text-sm font-medium text-accent-600 mb-2 uppercase tracking-wide">分类浏览</p>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            按分类查找网址
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            八大分类覆盖主流互联网平台，快速定位您需要的最新网址入口
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {categories.map((cat) => {
            const count = sites.filter((s) => s.category === cat.id).length;
            return (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="group p-5 md:p-6 rounded-xl bg-background-50 hover:bg-background-200/50 border border-background-200/70 hover:border-accent-300 transition-all duration-200 cursor-pointer text-center"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl bg-accent-100 mx-auto mb-3 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-xl md:text-2xl text-accent-600`}></i>
                </div>
                <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-1 whitespace-nowrap">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 mb-2 line-clamp-1">
                  {cat.description}
                </p>
                <span className="text-xs text-accent-600 font-medium">
                  {count} 个网址
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LatestArticlesSection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-3">
          <div>
            <p className="text-xs md:text-sm font-medium text-primary-600 mb-2 uppercase tracking-wide">最新资讯</p>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              最新网址推荐文章
            </h2>
          </div>
          <Link
            to="/category/search-engine"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
          >
            查看全部文章 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {articles.slice(0, 3).map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group p-5 md:p-6 rounded-xl bg-background-100 hover:bg-background-200/50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2.5 py-0.5 rounded-md text-[10px] font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                  {categories.find((c) => c.id === article.category)?.name || article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
              </div>
              <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                {article.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                {article.summary}
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleFaq = useCallback((id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs md:text-sm font-medium text-accent-600 mb-2 uppercase tracking-wide">常见问题</p>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            关于最新网址导航的常见问题
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            以下是用户最关心的问题，帮助您更好地使用最新网址导航服务
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
              >
                <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 pr-4">
                  {faq.question}
                </h3>
                <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-200 ${openId === faq.id ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                </div>
              </button>
              {openId === faq.id && (
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setStatus('success');
      setMessage('感谢订阅！您将第一时间收到最新网址更新通知。');
      return;
    }

    const formData = new FormData(form);
    const url = 'https://readdy.ai/api/form/d9u55a78l97n3eas3hjg';

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setStatus('success');
        setMessage('感谢订阅！您将第一时间收到最新网址更新通知。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '订阅失败，请稍后重试';
        setStatus('error');
        setMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setMessage('网络错误，请检查网络连接后重试');
    }
  }, []);

  return (
    <section id="newsletter" className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="rounded-2xl bg-background-100 border border-background-200/70 p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 mx-auto mb-4">
              <i className="ri-mail-send-line text-2xl text-primary-600"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              订阅最新网址更新
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              订阅后您将第一时间收到各大平台最新网址变更通知，再也不用担心找不到正确的官方入口
            </p>
          </div>

          <form ref={formRef} data-readdy-form="" onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入您的邮箱地址"
                required
                className="flex-1 px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              />
              <button
                type="submit"
                className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </div>
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            {status === 'success' && (
              <p className="mt-3 text-sm text-accent-600 text-center">{message}</p>
            )}
            {status === 'error' && (
              <p className="mt-3 text-sm text-red-500 text-center">{message}</p>
            )}
            <p className="mt-3 text-xs text-foreground-400 text-center">
              我们尊重您的隐私，不会发送垃圾邮件，您可以随时取消订阅
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const websiteSchema = {
    name: '最新网址导航',
    url: 'https://navlink2023.github.io/',
    description: '汇聚各大平台官方最新网址入口，实时更新、安全可靠。告别失效链接困扰，一键直达目标网站。',
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    name: '最新网址导航',
    url: 'https://navlink2023.github.io/',
    description: '最新网址导航 - 实时收录各大平台官方最新网址入口',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@latest-url-nav.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <SchemaOrg type="WebSite" data={websiteSchema} />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="WebPage" data={{ name: '首页 - 最新网址导航', url: 'https://navlink2023.github.io/', description: '最新网址导航首页，汇集热门网址推荐、分类导航、最新文章和常见问题' }} />
      <SchemaOrg type="FAQPage" data={faqSchema} />

      <Navbar />
      <main>
        <HeroSection />
        <HotSitesSection />
        <CategoryGridSection />
        <LatestArticlesSection />
        <FAQSection />
        <NewsletterSection />
      </main>
      <Footer />
    </>
  );
}